/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    Switch,
    makeStyles,
} from '@fluentui/react-components';
import { Column } from '../common/Column';
import { Row } from '../common/Row';
import { ContactPanel } from '../controls/ContactPanel';
import { AddressControl } from '../controls/AddressControl';
import { Contact, Party, Phone, useInitialValues } from '../../data/types';
import { PhonesPanel } from '../controls/PhonesPanel';
import { useDialogControls } from './useDialogControls';
import { usePartyService } from '../../data/services';
import { useValidation } from '../../data/types/validation';

const useStyles = makeStyles({
    root: {},
    field: {
        width: '100%',
    },
    addContactBtn: {
        fontSize: '13px',
        ':focus': {
            outline: 'none',
            boxShadow: 'none',
        },
    },
});

export type SelectContactsDialogProps = {
    title?: string;
    icon?: JSX.Element;
    children?: ReactElement<any>;
    onSubmit?: (contacts: Contact[]) => void;
    contactToEdit?: Contact;
    party?: Party;
    contacts?: Contact[];
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
    noCreate?: boolean;
    filter?: (contact: Contact) => boolean;
    noSelect?: boolean;
};
export const SelectContactsDialog = (props: SelectContactsDialogProps) => {
    const {
        title,
        icon,
        children,
        party,
        contacts,
        onSubmit,
        contactToEdit,
        open,
        onOpenChange,
        noCreate = false,
        filter = () => true,
        noSelect = false,
    } = props;

    //Hooks
    const initValues = useInitialValues();
    const partyService = usePartyService();
    const { validateContact } = useValidation();

    //State
    const [createNew, setCreateNew] = useState<boolean>(!!noSelect);
    const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
    const [phonesValid, setPhonesValid] = useState<boolean>(true);

    //Styles
    const styles = useStyles();

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //Form
    const getInitialValues = (): Contact => {
        if (!!contactToEdit) {
            return { ...initValues.contact(party?.partyId as string), ...contactToEdit };
        } else {
            return initValues.contact(party?.partyId as string);
        }
    };
    const form = useFormik({
        initialValues: getInitialValues(),
        onSubmit: async (values, { resetForm }) => {
            if (!createNew && !!selectedContacts.length) {
                !!onSubmit && onSubmit(selectedContacts);
            } else if (!!values.name) {
                //add new to contact api
                if(!!party){
                    await partyService.addContactToPartyAsync(party?.partyId as string, values as Contact, true);
                }

                !!onSubmit && onSubmit([{ ...values }]);
            }

            resetForm({ values: getInitialValues() });
            setSelectedContacts([]);
            setCreateNew(!!noSelect);
            setPhonesValid(true);
        },
        validationSchema: createNew ? validateContact.schema : undefined,
    });

    //UI Events
    const resetAll = () => {
        form.resetForm({ values: getInitialValues() });
        setSelectedContacts([]);
        setCreateNew(!!noSelect);
        setPhonesValid(true);
    };
    const clickCancel = (e: any) => {
        e.stopPropagation();
        resetAll();
    };
    const toggleCreateNew = (e: any) => {
        e.stopPropagation();
        setCreateNew((prev) => {
            return !prev;
        });
    };
    const onContactsSelected = (contacts: Contact[]) => {
        setSelectedContacts(contacts);
    };
    const onChangePhones = (phones: Phone[]) => {
        form.setFieldValue('phone', phones);
    };
    const clickSubmit = (e: any) => {
        e.stopPropagation();
        form.submitForm();
    };

    const getContacts = (): Contact[] => {
        let contactList: Contact[] = [];
        if(!!contacts && contacts.length > 0){
            contactList = contacts;
        } else if(!!party?.partyContacts && party.partyContacts.length > 0){
            contactList = party.partyContacts;
        }

        return contactList.filter(filter);
    };

    //Effects
    useEffect(() => {
        if (dialogOpen && !!contactToEdit) {
            form.resetForm({ values: getInitialValues() });
        }
    }, [dialogOpen, contactToEdit]);

    useEffect(() => {
        // const loadContacts = async () => {
        //     if (!!party?.partyId) {
        //         await contactService.loadContactsForPartyAsync(party.partyId);
        //     }
        // };

        if (dialogOpen && !!party?.partyId) {
            form.setFieldValue('partyId', party?.partyId);
            //loadContacts();
        }
    }, [dialogOpen, party?.partyId]);

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined} style={{ maxWidth: '700px' }}>
                <DialogBody>
                    <DialogTitle
                        action={
                            <>
                                {!noCreate && (
                                    <Switch
                                        label='Create New Contact'
                                        checked={createNew}
                                        onChange={toggleCreateNew}
                                        data-testid='select-contacts-create-new-contact-link'
                                    />
                                )}
                            </>
                        }
                    >
                        <Row gap='medium'>
                            <Column width='auto' vAlign='middle'>
                                {icon}
                            </Column>
                            <Column vAlign='middle'>{title}</Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        {!createNew ? (
                            <Column padding='pl-2 pb-3'>
                                <ContactPanel
                                    contacts={getContacts()}
                                    selectable
                                    onSelectionChanged={onContactsSelected}
                                    noRemove
                                />
                            </Column>
                        ) : (
                            <>
                                <Column>
                                    <Row>
                                        <Column>
                                            <Field className={styles.field} label='Name' required validationMessage={form.errors.name}>
                                                <Input
                                                    name='name'
                                                    value={form.values.name || ''}
                                                    onChange={form.handleChange}
                                                    placeholder='Enter the name of the Contact'
                                                    data-testid='select-contacts-name-input'
                                                />
                                            </Field>
                                        </Column>
                                        <Column>
                                            <Field className={styles.field} label='Title' validationMessage={form.errors.title}>
                                                <Input
                                                    name='title'
                                                    value={form.values.title || ''}
                                                    onChange={form.handleChange}
                                                    placeholder='Enter the title of the Contact'
                                                    data-testid='select-contacts-title-input'
                                                />
                                            </Field>
                                        </Column>
                                    </Row>
                                    <Field className={styles.field} label='Email' validationMessage={form.errors.email}>
                                        <Input
                                            name='email'
                                            value={form.values.email || ''}
                                            onChange={form.handleChange}
                                            placeholder='Enter the email of the Contact'
                                            data-testid='select-contacts-email-input'
                                        />
                                    </Field>
                                    <AddressControl
                                        address={form.values.address}
                                        errors={form.errors.address}
                                        onChange={form.handleChange}
                                        testIdPrefix='select-contacts'
                                    />
                                    <PhonesPanel
                                        phones={form.values.phone}
                                        onChangePhones={onChangePhones}
                                        onValidChange={setPhonesValid}
                                    />
                                </Column>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                type='submit'
                                appearance='primary'
                                onClick={clickSubmit}
                                disabled={
                                    (!!createNew && (!form.dirty || !phonesValid || !form.isValid)) ||
                                    (!createNew && !selectedContacts.length)
                                }
                                data-testid='select-contacts-submit-button'
                            >
                                {!!createNew ? 'Add New Contact' : 'Select Contacts'}
                            </Button>
                        </DialogTrigger>
                        <DialogTrigger disableButtonEnhancement>
                            <Button appearance='secondary' onClick={clickCancel} data-testid='select-contacts-cancel-button'>
                                Cancel
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
